import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapSuiteDetailDto, mapSuiteListItemDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mapMetricSorts } from '@/shared/data/metricSortFields';
import {
  buildEqFilter,
  buildSlugFilters,
  mergeFilters,
  type GridFilters,
} from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  SuiteDetailDto,
  SuiteFilterCountsDto,
  SuiteListItemDto,
  SuiteSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const querySuites = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    search?: string;
    metricsStartDay?: string;
    metricsEndDay?: string;
    previousMetricsStartDay?: string;
    previousMetricsEndDay?: string;
    include?: SuiteInclude;
  },
): Promise<QueryResult<SuiteListItemDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters = !hasProjectFilter ? buildEqFilter('projectId', projectId ?? null) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts
    ? mapMetricSorts(input.sorts)
    : [{ field: 'createdAt', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<SuiteListItemDto>>(
      'suites/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        search: input?.search,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        previousMetricsStartDay: input?.previousMetricsStartDay,
        previousMetricsEndDay: input?.previousMetricsEndDay,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapSuiteListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const querySuiteSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsStartDay?: string;
    metricsEndDay?: string;
  },
): Promise<SuiteSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
  const projectFilters = !hasProjectFilter ? buildEqFilter('projectId', projectId ?? null) : [];
  const combinedFilters = mergeFilters(projectFilters, filters);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<SuiteSummaryDto>(
      'suites/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const querySuiteFilterCounts = async (
  projectId: string,
  organisationId: string | null | undefined,
  input: {
    search?: string;
    metricsStartDay?: string;
    metricsEndDay?: string;
    filters: {
      suites: QueryFilter[];
      statuses: QueryFilter[];
      tags: QueryFilter[];
    };
  },
): Promise<SuiteFilterCountsDto> => {
  const token = ensureToken();
  const withProjectFilter = (filters: QueryFilter[]): QueryFilter[] => {
    const hasProjectFilter = filters.some((filter) => filter.field === 'projectId');
    return hasProjectFilter
      ? filters
      : mergeFilters(buildEqFilter('projectId', projectId), filters);
  };
  try {
    return await apiClient.post<SuiteFilterCountsDto>(
      'suites/filter-counts',
      {
        search: input.search,
        metricsStartDay: input.metricsStartDay,
        metricsEndDay: input.metricsEndDay,
        filters: {
          suites: withProjectFilter(input.filters.suites),
          statuses: withProjectFilter(input.filters.statuses),
          tags: withProjectFilter(input.filters.tags),
        },
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getSuiteBySlug = async (
  slug: string | null,
  projectId?: string | null,
  organisationId?: string | null,
): Promise<SuiteDetailDto | null> => {
  if (!slug) {
    return null;
  }
  const token = ensureToken();
  const filters = buildSlugFilters(slug);
  try {
    const payload = await apiClient.post<QueryResult<SuiteListItemDto>>(
      'suites/query',
      { page: 1, pageSize: 1, filters },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    const mapped = mapQueryResult(payload, mapSuiteListItemDto);
    if (mapped.items.length > 0) {
      return await getSuiteById(mapped.items[0].id, organisationId);
    }
    return null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw toApiError(err);
  }
};

export const getSuiteById = async (
  id: string,
  organisationId?: string | null,
): Promise<SuiteDetailDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<SuiteDetailDto>(`suites/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapSuiteDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createSuite = async (
  input: {
    name: string;
    projectId: string;
    featureIds?: string[];
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<SuiteDetailDto> => {
  const token = ensureToken();
  const body = {
    name: input.name.trim(),
    projectId: input.projectId.trim(),
    featureIds: input.featureIds,
    tags: input.tags,
  };
  try {
    const result = await apiClient.post<SuiteDetailDto>('suites', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapSuiteDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateSuite = async (
  input: {
    id: string;
    name?: string;
    featureIds?: string[];
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<SuiteDetailDto> => {
  const token = ensureToken();
  const body = {
    name: input.name?.trim(),
    featureIds: input.featureIds,
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<SuiteDetailDto>(`suites/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapSuiteDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type SuiteInclude = {
  project?: boolean;
  features?: boolean;
  latestRun?: boolean;
  executionStatusCounts?: boolean;
  dailyHistory?: boolean;
  executionSummaries?: boolean;
  latestTestResults?: { counts?: boolean; summaries?: boolean };
  metrics?: boolean;
  metricDeltas?: boolean;
};

export const deleteSuite = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`suites/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
